import React from "react";
import type {
    InvestmentTransferRequest,
    InvestmentTransferSummary,
} from "../types/investments";

interface InvestmentTransferRequestsProps {
    requests?: InvestmentTransferRequest[];
    summary?: InvestmentTransferSummary;
    isLoading: boolean;
    onRefresh: () => void;
    onConfirmAll: () => void;
    onConfirm: (request: InvestmentTransferRequest) => void;
    onCancel: (request: InvestmentTransferRequest) => void;
    isConfirming?: boolean;
    pendingKey?: string | null;
}

const formatCurrency = (value: number): string =>
    `$${value.toFixed(2)}`;

export const InvestmentTransferRequests: React.FC<
    InvestmentTransferRequestsProps
> = ({
    requests,
    summary,
    isLoading,
    onRefresh,
    onConfirmAll,
    onConfirm,
    onCancel,
    isConfirming = false,
    pendingKey = null,
}) => {
    const hasRequests = (requests?.length ?? 0) > 0;

    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Заявки на переводы между стратегиями
                    </h2>
                    {summary && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Всего заявок: {summary.count} · Общая сумма:{" "}
                            {formatCurrency(summary.total_amount)}
                        </p>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={onRefresh}
                        className="text-sm px-4 py-2 rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        Обновить
                    </button>
                    {hasRequests && (
                        <button
                            onClick={onConfirmAll}
                            disabled={isConfirming}
                            className={`text-sm px-4 py-2 rounded-md text-white font-medium ${
                                isConfirming
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-blue-600 hover:bg-blue-700"
                            }`}
                        >
                            {isConfirming
                                ? "Подтверждение..."
                                : "Подтвердить все"}
                        </button>
                    )}
                </div>
            </div>

            {isLoading ? (
                <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                    Загрузка...
                </div>
            ) : !hasRequests ? (
                <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                    Нет заявок на переводы
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Клиент
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Откуда
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Куда
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Сумма
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Действия
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {requests?.map((request, index) => {
                                const key = `${request.user_id}-${request.from_strategy}-${request.to_strategy}-${request.amount}-${index}`;
                                const disabled = pendingKey === key;
                                const user = request.user;

                                return (
                                    <tr key={key}>
                                        <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                            <div className="font-semibold">
                                                {user?.surname} {user?.name}
                                                <span className="text-gray-500 dark:text-gray-400 text-xs ml-2">
                                                    ID: {request.user_id}
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {user?.email || "E-mail не указан"}
                                            </div>
                                            {user?.telegram_id && (
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    Telegram: {user.telegram_id}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                            <span
                                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                                style={{
                                                    backgroundColor: `${request.from_color}15`,
                                                    color: request.from_color,
                                                }}
                                            >
                                                Стратегия {request.from_strategy}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                            <span
                                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                                style={{
                                                    backgroundColor: `${request.to_color}15`,
                                                    color: request.to_color,
                                                }}
                                            >
                                                Стратегия {request.to_strategy}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-right text-gray-900 dark:text-white font-semibold">
                                            {formatCurrency(request.amount)}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => onConfirm(request)}
                                                    disabled={disabled}
                                                    className={`px-3 py-1.5 rounded-md text-white text-xs font-medium ${
                                                        disabled
                                                            ? "bg-gray-400 cursor-not-allowed"
                                                            : "bg-green-600 hover:bg-green-700"
                                                    }`}
                                                >
                                                    {disabled
                                                        ? "Обработка..."
                                                        : "Подтвердить"}
                                                </button>
                                                <button
                                                    onClick={() => onCancel(request)}
                                                    disabled={disabled}
                                                    className={`px-3 py-1.5 rounded-md text-white text-xs font-medium ${
                                                        disabled
                                                            ? "bg-gray-400 cursor-not-allowed"
                                                            : "bg-red-600 hover:bg-red-700"
                                                    }`}
                                                >
                                                    {disabled
                                                        ? "Обработка..."
                                                        : "Отменить"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

